import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isVideo = (url = '') => /\.(mp4|mov|webm|m4v|avi)(\?|$)/i.test(url);

const AdminGallery = () => {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setImages(response.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // 50 MB hard cap (object storage / FastAPI memory friendly)
    const MAX_BYTES = 50 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      toast.error(`File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max is 50 MB.`);
      e.target.value = '';
      return;
    }

    const token = localStorage.getItem('admin_token');
    if (!token) {
      toast.error('You need to be logged in. Please log in again.');
      navigate('/admin/login');
      return;
    }

    setUploading(true);
    setProgress(0);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const uploadResponse = await axios.post(`${API}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}` },
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded * 100) / evt.total));
        },
      });

      const fileUrl = `${BACKEND_URL}${uploadResponse.data.url}`;

      await axios.post(
        `${API}/gallery`,
        {
          url: fileUrl,
          alt: file.name || 'Gallery item',
          style: 'Braids',
          order: images.length,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await fetchImages();
      toast.success(`Uploaded "${file.name}" successfully`);
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || error.message || 'Unknown error';
      console.error('Upload failed:', status, detail);
      if (status === 401) {
        toast.error('Session expired. Please log in again.');
        navigate('/admin/login');
      } else if (status === 413) {
        toast.error('File is too large.');
      } else {
        toast.error(`Upload failed: ${detail}`);
      }
    } finally {
      setUploading(false);
      setProgress(0);
      e.target.value = '';
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/gallery/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchImages();
      toast.success('Deleted');
    } catch (error) {
      const detail = error?.response?.data?.detail || error.message;
      toast.error(`Failed to delete: ${detail}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => navigate('/admin/dashboard')} data-testid="back-dashboard-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <h1 className="text-2xl font-bold text-gray-900">Manage Gallery</h1>
            </div>

            <label className="cursor-pointer">
              <Button
                className="bg-amber-800 hover:bg-amber-900"
                disabled={uploading}
                data-testid="upload-btn"
                onClick={(e) => e.currentTarget.parentElement.querySelector('input')?.click()}
                type="button"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? `Uploading… ${progress}%` : 'Upload Photo or Video'}
              </Button>
              <input
                type="file"
                accept="image/*,video/*"
                className="hidden"
                onChange={handleUpload}
                disabled={uploading}
                data-testid="upload-input"
              />
            </label>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {images.map((item) => (
            <Card key={item.id} className="overflow-hidden group">
              <div className="relative aspect-square bg-black">
                {isVideo(item.url) ? (
                  <video
                    src={item.url}
                    className="w-full h-full object-cover"
                    controls
                    preload="metadata"
                  />
                ) : (
                  <img src={item.url} alt={item.alt} className="w-full h-full object-cover" />
                )}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center pointer-events-none">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                    className="pointer-events-auto"
                    data-testid={`delete-${item.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {images.length === 0 && (
          <Card className="p-12 text-center">
            <p className="text-gray-500 text-lg">
              No items yet. Click "Upload Photo or Video" to add your first gallery item.
            </p>
          </Card>
        )}

        <Card className="mt-8 bg-amber-50 border-amber-200">
          <CardContent className="p-6 space-y-2">
            <p className="text-gray-700">
              💡 <strong>Tip:</strong> Upload high-quality photos and short videos of your best work.
              Hover any item to delete it.
            </p>
            <p className="text-gray-600 text-sm">
              Supported: JPG, PNG, WEBP, GIF, MP4, MOV, WEBM. Max file size: 50&nbsp;MB.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminGallery;
